import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type { IssueNavigatorViewId } from '../../common/types.tsx';
import type { useSelectedViewMutation as updateViewLayoutMutation } from './__generated__/useSelectedViewMutation.graphql';
import type {
	JiraIssueNavigatorSearchLayout,
	useSelectedViewMutationOldMutation as updateUserViewModeMutation,
} from './__generated__/useSelectedViewMutationOldMutation.graphql';

export const viewModeToSearchLayout = (
	view: IssueNavigatorViewId,
): JiraIssueNavigatorSearchLayout => (view.startsWith('list_') ? 'LIST' : 'DETAIL');
/**
 * Maps the provided issue navigator view mode into the searchLayout required by the JiraUserPreferencesMutation.setIssueNavigatorSearchLayout GraphQL node.
 */
export const useSelectedViewMutation = () => {
	const userCloudId = useCloudId();
	const [commitMutation] = useMutation<updateUserViewModeMutation>(graphql`
		mutation useSelectedViewMutationOldMutation(
			$cloudId: ID!
			$searchLayout: JiraIssueNavigatorSearchLayout
		) {
			jira {
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					setIssueNavigatorSearchLayout(searchLayout: $searchLayout) {
						success
						errors {
							message
						}
						issueNavigatorSearchLayout
					}
				}
			}
		}
	`);

	const [commit] = useMutation<updateViewLayoutMutation>(graphql`
		mutation useSelectedViewMutation($input: JiraSetIssueSearchViewLayoutInput!) {
			jira_setIssueSearchViewLayout(input: $input) {
				success
				errors {
					message
				}
			}
		}
	`);

	return useCallback(
		(view: IssueNavigatorViewId, viewARI: string) => {
			if (fg('saved_list_view_technical_changes')) {
				commit({
					variables: {
						input: {
							viewId: viewARI,
							viewLayout: viewModeToSearchLayout(view),
						},
					},
					onError: (error) => {
						fireErrorAnalytics({
							sendToPrivacyUnsafeSplunk: true,
							error,
							meta: {
								id: 'mainSelectedViewMutation',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
						});
					},
					optimisticResponse: {
						jira_setIssueSearchViewLayout: {
							success: true,
							errors: [],
						},
					},
				});
			} else {
				commitMutation({
					variables: {
						cloudId: userCloudId,
						searchLayout: viewModeToSearchLayout(view),
					},
					onError: (error) => {
						fireErrorAnalytics({
							sendToPrivacyUnsafeSplunk: true,
							error,
							meta: {
								id: 'mainSelectedViewMutation',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
						});
					},
					optimisticResponse: {
						jira: {
							userPreferences: {
								setIssueNavigatorSearchLayout: {
									success: true,
									errors: [],
									issueNavigatorSearchLayout: viewModeToSearchLayout(view),
								},
							},
						},
					},
				});
			}
		},
		[commit, commitMutation, userCloudId],
	);
};
